// Republic Atlas - Badge Component

import { HTMLAttributes, forwardRef } from 'react';
import { cn, getPartyBadgeClass } from '@/utils';
import type { PoliticalParty } from '@/types';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'party' | 'outline' | 'secondary';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  party?: PoliticalParty;
  size?: 'sm' | 'md';
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800 border-gray-200',
  success: 'bg-green-100 text-green-800 border-green-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  party: '', // Will be set dynamically
  outline: 'bg-transparent text-gray-600 border-gray-300',
  secondary: 'bg-slate-100 text-slate-700 border-slate-200',
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', party, size = 'md', className, children, ...props }, ref) => {
    const variantClass = variant === 'party' && party
      ? getPartyBadgeClass(party)
      : variantClasses[variant];

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium rounded-full border',
          variantClass,
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;

